import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { layoutImplicitHydrogens } from '@alchemiesolns/layout2d';
import { inferLonePairs } from './inferLonePairs';

/**
 * Standard bond counts (typical number of bonds) for common elements.
 * Used to determine how many implicit hydrogens an atom needs.
 */
const STANDARD_BOND_COUNT: Record<string, number> = {
  H: 1,
  B: 3,
  C: 4,
  N: 3,
  O: 2,
  F: 1,
  Si: 4,
  P: 3,
  S: 2,
  Cl: 1,
  Br: 1,
  I: 1,
  Se: 2,
  As: 3,
  Te: 2,
};

export interface InferHydrogenOptions {
  /** Bond length for positioning H atoms */
  bondLength?: number;
  /** Atom IDs to skip (e.g., atoms with explicit NumHydrogens from CDXML) */
  skipAtomIds?: Set<string>;
}

/**
 * Infers and adds implicit hydrogen atoms to satisfy standard valences.
 *
 * For each non-hydrogen atom, computes:
 *   implicitH = standardBondCount - existingBondOrderSum + formalCharge
 *
 * Positive formal charge increases bonds (e.g., NH4+ has 4 bonds on N).
 * Negative formal charge decreases bonds (e.g., O- has 1 bond).
 *
 * Skips atoms with unknown standard bond counts (metals, etc.).
 * Uses VSEPR-aware placement from @alchemiesolns/layout2d.
 *
 * @param workspace - Workspace to process
 * @param options - Optional configuration (bondLength for placement)
 * @returns New workspace with implicit hydrogens added and positioned
 */
export function inferImplicitHydrogens(
  workspace: MolecularWorkspace,
  options?: InferHydrogenOptions
): MolecularWorkspace {
  let ws = workspace;
  const parentAtomIds: string[] = [];

  // Step 1: Add H atoms and bonds
  for (const atom of workspace.getAllAtoms()) {
    if (atom.symbol === 'H') continue;
    if (options?.skipAtomIds?.has(atom.id)) continue;

    const standardBonds = STANDARD_BOND_COUNT[atom.symbol];
    if (standardBonds === undefined) continue;

    let bondOrderSum = 0;
    for (const bond of ws.getAllBonds()) {
      if (bond.a1.id === atom.id || bond.a2.id === atom.id) {
        bondOrderSum += bond.order;
      }
    }

    const neededH = Math.max(0, standardBonds - bondOrderSum + atom.charge);
    if (neededH === 0) continue;

    parentAtomIds.push(atom.id);
    for (let i = 0; i < neededH; i++) {
      const { workspace: wsWithH, atomId: hAtomId } = ws.addAtom({
        symbol: 'H',
        x: atom.x,
        y: atom.y,
        charge: 0,
        lonePairs: 0,
        radicals: 0,
      });
      ws = wsWithH.addBond({ order: 1, bondType: 'l' }, atom.id, hAtomId).workspace;
    }
  }

  if (parentAtomIds.length === 0) return workspace;

  // Step 2: Infer lone pairs (needed for VSEPR placement)
  ws = inferLonePairs(ws);

  // Step 3: Position H atoms with VSEPR geometry
  ws = layoutImplicitHydrogens(ws, parentAtomIds, { bondLength: options?.bondLength });

  return ws;
}
